import type { UtilityContext } from '@change-corgi/core-react/utilityContext';

import type { PrefetchContext, PrefetchedDataProps, PrefetchedUserDataProps } from '#src/shared/prefetch';

import { isSunriseDashboardPageAccessExperimentEnabled } from '#src/app/pages/starterDashboard/shared/utils';
import { useCountryCode, useLocale } from '#src/app/shared/hooks/l10n';

import { prefetchStarterDashboardPageDataFromApi, useStarterDashboardPageDataFromApi } from './pageData';

export type StarterDashboardPagePrefetchedContext = Readonly<{
	pageData: Parameters<typeof useStarterDashboardPageDataFromApi>[0];
}>;

export type StarterDashboardPageCombinedState = ReturnType<typeof useStarterDashboardPageDataFromApi>;

export function usePageContext(
	slug: string,
	prefetchedData?: StarterDashboardPagePrefetchedContext,
): StarterDashboardPageCombinedState {
	const locale = useLocale();
	const countryCode = useCountryCode();
	return useStarterDashboardPageDataFromApi(prefetchedData?.pageData, { slug, countryCode, locale });
}

export async function prefetchPageContext(context: PrefetchContext): Promise<StarterDashboardPagePrefetchedContext> {
	const pageData = await prefetchStarterDashboardPageDataFromApi(context, {
		// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
		slug: context.params.slug!,
		countryCode: context.l10n.countryCode,
		locale: context.l10n.locale,
	});

	return { pageData };
}

type StarterDashboardPrefetchedUserContext = { sunriseDashboardPageAccessEnabled: boolean };

export async function prefetchUserInfo(utilityContext: UtilityContext): Promise<StarterDashboardPrefetchedUserContext> {
	const sunriseDashboardPageAccessEnabled = await isSunriseDashboardPageAccessExperimentEnabled(utilityContext);
	return { sunriseDashboardPageAccessEnabled };
}

export type StarterDashboardPagePrefetchedDataProps = PrefetchedDataProps<StarterDashboardPagePrefetchedContext> &
	PrefetchedUserDataProps<StarterDashboardPrefetchedUserContext>;
